import { useState, type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { LoadingSpinner } from '../elements/LoadingSpinner';
import { Body } from '../typography/Body';
import { H3 } from '../typography/HeadingLevels';

const surfaceBackground = `color-mix(in srgb, ${colors.light} 45%, ${colors.white})`;

const BASE_WEEKS = 8;

/** Hero image radius as drawn (Figma `26652:145632`, ERS-61 A4) — between `radii.md` and `radii.lg`. */
const heroRadius = '10px';
/** Thumbnail strip gap/radius as drawn (Figma `26652:145632`, ERS-61 A4). */
const thumbnailGap = '5px';
const thumbnailRadius = '5px';

const Root = styled.section<{ $boxed: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space.md};
  min-width: 0;
  ${({ $boxed }) =>
    $boxed
      ? css`
          padding: ${space.lg} ${space.md};
          border-radius: ${radii.lg};
          background: ${surfaceBackground};
          box-shadow: 0 1px 2px ${getMediumRGBA(0.12)};
        `
      : css`
          padding: 0;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
        `}
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: ${heroRadius};
`;

const HeroPlaceholder = styled.div`
  width: 100%;
  height: 180px;
  border-radius: ${heroRadius};
  background: ${colors.light};
`;

const LoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  width: 100%;
`;

const ThumbnailRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: ${thumbnailGap};
  width: 100%;
  min-width: 0;
`;

const ThumbnailButton = styled.button<{ $active: boolean }>`
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  flex: 1 1 0;
  min-width: 0;
  border-radius: ${thumbnailRadius};
  background: transparent;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? `0 0 1px 1px ${colors.primary}` : 'none')};

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const ThumbnailImg = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: ${thumbnailRadius};
`;

const SegmentsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2px;
  width: 100%;
  min-width: 0;
`;

const SegmentColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space.xs};
`;

const SegmentLabel = styled.span<{ $active: boolean }>`
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: ${text.body.fontWeight};
  color: ${({ $active }) => ($active ? colors.dark : colors.medium)};
  text-align: center;
  width: 100%;
`;

const SegmentBar = styled.div<{
  $active: boolean;
  $segmentIndex: number;
  $segmentCount: number;
}>`
  width: 100%;
  height: 10px;
  box-sizing: border-box;
  background: ${({ $active }) => ($active ? colors.primary : colors.light)};
  border-top-left-radius: ${({ $segmentIndex }) => ($segmentIndex === 0 ? radii.sm : '0')};
  border-bottom-left-radius: ${({ $segmentIndex }) => ($segmentIndex === 0 ? radii.sm : '0')};
  border-top-right-radius: ${({ $segmentIndex, $segmentCount }) =>
    $segmentIndex === $segmentCount - 1 ? radii.sm : '0'};
  border-bottom-right-radius: ${({ $segmentIndex, $segmentCount }) =>
    $segmentIndex === $segmentCount - 1 ? radii.sm : '0'};
`;

const DatesRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${space.xs};
  width: 100%;
  min-width: 0;
`;

const DateCell = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: center;
`;

const DashCell = styled.div`
  flex: 0 0 auto;
`;

export type TrainingSummaryProgressMode = 'weeks' | 'bonus';

export type TrainingSummaryProps = Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  /** Resolved hero/cover image URL (caller supplies; e.g. from Firestore or static asset). */
  heroImageSrc?: string;
  /** Resolved thumbnail URLs, typically one per week (caller supplies). */
  thumbnailSrcs?: readonly string[];
  /** 800px hero URLs per thumbnail index; same length/order as thumbnailSrcs when provided. */
  heroImageSrcs?: readonly string[];
  /** Caller-driven loading UI (e.g. while fetching URLs from Firestore). */
  isLoading?: boolean;
  /** Caller-driven error message (e.g. failed fetch). */
  loadErrorMessage?: string;
  /**
   * Mirrors native `CourseProgressBar` / `getUserWeeksInCourse`: week column `i` is filled when `i <= weeksInCourse`.
   * Clamped to `0 … BASE_WEEKS - 1`. Ignored when `progressMode === 'bonus'`.
   */
  weeksInCourse?: number;
  progressMode?: TrainingSummaryProgressMode;
  bonusWeek9Complete?: boolean;
  bonusWeek10Complete?: boolean;
  dateStart?: string;
  dateEnd?: string;
  /** `null` omits the heading — for a parent that already titles the block itself. */
  title?: string | null;
  /**
   * When `false`, omits card chrome (padding, surface background, radius, shadow) so the block
   * sits flush in a parent layout. Default `true` preserves the standalone card look.
   */
  boxed?: boolean;
  /**
   * When `false`, omits the week/bonus progress dashes row (and the date range below it) — not
   * part of the Welcome design (ERS-61 A4). Default `true` preserves existing BNPL call sites.
   */
  showProgress?: boolean;
};

function clampWeeksInCourse(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.max(0, Math.min(BASE_WEEKS - 1, Math.floor(value)));
}

function progressAriaLabel(
  mode: TrainingSummaryProgressMode,
  weeksInCourse: number,
  bonus9: boolean,
  bonus10: boolean,
): string {
  if (mode === 'bonus') {
    const parts: string[] = ['Fortschritt: Hauptprogramm abgeschlossen'];
    if (bonus9) {
      parts.push('Bonuswoche 9 abgeschlossen');
    }
    if (bonus10) {
      parts.push('Bonuswoche 10 abgeschlossen');
    }
    return parts.join(', ');
  }
  const filled = Math.min(BASE_WEEKS, weeksInCourse + 1);
  return `Fortschritt: ${filled} von ${BASE_WEEKS} Wochen`;
}

function resolveHeroForThumbIndex(
  index: number,
  heroImageSrcs: readonly string[] | undefined,
  thumbnailSrcs: readonly string[],
  fallbackHero: string,
): string {
  const hero = heroImageSrcs?.[index]?.trim();
  if (hero) {
    return hero;
  }
  const thumb = thumbnailSrcs[index]?.trim();
  if (thumb) {
    return thumb;
  }
  return fallbackHero;
}

function inferInitialThumbIndex(trimmedHero: string, heroImageSrcs: readonly string[] | undefined): number | null {
  if (!trimmedHero || !heroImageSrcs?.length) {
    return null;
  }
  const matchIndex = heroImageSrcs.findIndex((src) => src.trim() === trimmedHero);
  return matchIndex >= 0 ? matchIndex : null;
}

type TrainingSummaryImagesProps = {
  trimmedHero: string;
  thumbnailSrcs: readonly string[];
  heroImageSrcs?: readonly string[];
};

function TrainingSummaryImages({ trimmedHero, thumbnailSrcs, heroImageSrcs }: TrainingSummaryImagesProps) {
  const [selectedThumbIndex, setSelectedThumbIndex] = useState<number | null>(() =>
    inferInitialThumbIndex(trimmedHero, heroImageSrcs),
  );

  const showThumbnails = thumbnailSrcs.length > 0;
  const displayHero =
    selectedThumbIndex !== null
      ? resolveHeroForThumbIndex(selectedThumbIndex, heroImageSrcs, thumbnailSrcs, trimmedHero)
      : trimmedHero;
  const heroAlt =
    selectedThumbIndex !== null ? `Vorschau Woche ${selectedThumbIndex + 1}` : displayHero ? 'Kursvorschau' : '';

  return (
    <>
      {displayHero ? <HeroImage src={displayHero} alt={heroAlt} /> : <HeroPlaceholder aria-hidden />}
      {showThumbnails ? (
        <ThumbnailRow role="group" aria-label="Trainingsbilder Vorschau">
          {thumbnailSrcs.map((src, index) => (
            <ThumbnailButton
              key={`${src}-${index}`}
              type="button"
              $active={selectedThumbIndex === index}
              aria-pressed={selectedThumbIndex === index}
              aria-label={`Woche ${index + 1}`}
              onClick={() => setSelectedThumbIndex(index)}
            >
              <ThumbnailImg src={src} alt="" />
            </ThumbnailButton>
          ))}
        </ThumbnailRow>
      ) : null}
    </>
  );
}

export function TrainingSummary({
  heroImageSrc,
  thumbnailSrcs = [],
  heroImageSrcs,
  isLoading = false,
  loadErrorMessage,
  weeksInCourse = 0,
  progressMode = 'weeks',
  bonusWeek9Complete = false,
  bonusWeek10Complete = false,
  dateStart,
  dateEnd,
  title = 'Dein Kurs',
  boxed = true,
  showProgress = true,
  className,
  ...rest
}: TrainingSummaryProps) {
  const trimmedHero = heroImageSrc?.trim() ?? '';
  const imageSetKey = `${trimmedHero}|${thumbnailSrcs.join('|')}|${heroImageSrcs?.join('|') ?? ''}`;

  const clampedWeeks = clampWeeksInCourse(weeksInCourse);

  const weekItems =
    progressMode === 'weeks'
      ? Array.from({ length: BASE_WEEKS }, (_, index) => ({
          key: `w-${index}`,
          label: String(index + 1),
          active: index <= clampedWeeks,
        }))
      : [
          { key: 'b-0', label: '1 - 8', active: true as const },
          { key: 'b-1', label: '9', active: bonusWeek9Complete },
          { key: 'b-2', label: '10', active: bonusWeek10Complete },
        ];

  const showDates = Boolean(dateStart && dateEnd);

  // `null` means the parent already provides the heading (welcome screen); an empty H3 would still
  // occupy a row, so the element is dropped rather than rendered blank.
  const heading = title == null || title === '' ? null : <H3>{title}</H3>;

  if (loadErrorMessage) {
    return (
      <Root $boxed={boxed} className={className} {...rest}>
        {heading}
        <Body tone="muted" variant="document">
          {loadErrorMessage}
        </Body>
      </Root>
    );
  }

  if (isLoading) {
    return (
      <Root $boxed={boxed} className={className} {...rest}>
        {heading}
        <LoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </LoadingArea>
      </Root>
    );
  }

  return (
    <Root $boxed={boxed} className={className} {...rest}>
      {heading}
      <TrainingSummaryImages
        key={imageSetKey}
        trimmedHero={trimmedHero}
        thumbnailSrcs={thumbnailSrcs}
        heroImageSrcs={heroImageSrcs}
      />
      {showProgress ? (
        <>
          <SegmentsRow
            role="group"
            aria-label={progressAriaLabel(progressMode, clampedWeeks, bonusWeek9Complete, bonusWeek10Complete)}
          >
            {weekItems.map((item, segmentIndex) => (
              <SegmentColumn key={item.key}>
                <SegmentLabel $active={item.active}>{item.label}</SegmentLabel>
                <SegmentBar $active={item.active} $segmentIndex={segmentIndex} $segmentCount={weekItems.length} />
              </SegmentColumn>
            ))}
          </SegmentsRow>
          {showDates ? (
            <DatesRow>
              <DateCell>
                <Body tone="muted" size="small" variant="document">
                  {dateStart}
                </Body>
              </DateCell>
              <DashCell>
                <Body tone="muted" size="small" variant="document">
                  –
                </Body>
              </DashCell>
              <DateCell>
                <Body tone="muted" size="small" variant="document">
                  {dateEnd}
                </Body>
              </DateCell>
            </DatesRow>
          ) : null}
        </>
      ) : null}
    </Root>
  );
}
